import { apiClient } from "@/services/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { accountKeys } from "@/hooks/api/useAccounts";

// Query keys
export const dashboardKeys = {
  all: ["dashboard"] as const,
  summary: () => [...dashboardKeys.all, "summary"] as const,
  accountsByType: () => [...dashboardKeys.all, "accountsByType"] as const,
  monthlySpending: (month?: string, year?: number) =>
    [...dashboardKeys.all, "monthlySpending", { month, year }] as const,
  recentTransactions: (options?: {
    limit?: number;
    days?: number;
    accountId?: number;
    isReviewed?: boolean;
  }) => [...dashboardKeys.all, "recentTransactions", options] as const,
};

// Hooks
export function useDashboardSummary() {
  return useQuery({
    queryKey: dashboardKeys.summary(),
    queryFn: () => apiClient.getDashboardSummary(),
    staleTime: 1000 * 60 * 2, // Dashboard data is stale after 2 minutes
  });
}

export function useMonthlySpending(month?: string, year?: number) {
  return useQuery({
    queryKey: dashboardKeys.monthlySpending(month, year),
    queryFn: () => apiClient.getMonthlySpendingSummary(month, year),
    staleTime: 1000 * 60 * 5,
  });
}

// Helper hook to refresh all dashboard data
export function useRefreshDashboard() {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    await queryClient.invalidateQueries({ queryKey: accountKeys.all });
  };
}
